<template>
  <div class="ann-page" :class="annFontClass">
    <div class="ann-inner">
      <AnnLang />
      <!-- ===== 视觉区块：752 设计稿，区块间流式堆叠、区块内绝对定位 ===== -->
      <Hero
        :fireCount="activityInfo?.totalFireCount ?? 0"
        :stages="globalStages"
        :uid="actUserInfo.uid || actUserInfo.cpUserId || ''"
        :isLoggedIn="isLoggedIn"
        @login="showLogin = true"
        @logout="onLogout"
        @howToPlay="openHowToPlayDialog"
        @recharge="openRecharge"
        @service="openService"
        @receive="handleReceiveGlobal"
      />
      <Contribute
        :fireCount="activityInfo?.availableFireCount ?? 0"
        :showBadge="hasClaimableTask"
        @ignite="handleIgnite"
        @supply="openSupplyDialog"
        @rally="openRallyDialog"
        @history="openHistoryDialog"
      />
      <Milestone :list="displayMilestones" @receive="handleReceiveMilestone" />
      <AnnFooter />
    </div>

    <LoginDialog v-model="showLogin" @loginSuccess="onLoginSuccess" />
    <PowerContributedDialog
      v-model="showPowerContributed"
      :count="powerContributedCount"
    />
    <HowToPlayDialog v-model="showHowToPlay" />
    <CongratulationDialog
      v-model="showCongratulation"
      :rewards="congratulationRewards"
      :title-text="congratulationTitleText"
    />
    <RewardHistoryDialog
      v-model="showRewardHistory"
      :records="rewardRecords"
    />
    <FlameSupplyDialog
      v-model="showFlameSupply"
      :tasks="displayTasks"
      @share="openShareDialog"
      @receive="handleReceiveTask"
    />
    <EliteRallyDialog
      v-model="showEliteRally"
      :recall-info="recallInfo"
      @receive="handleReceiveRecall"
    />
    <ShareDialog v-model="showShare" @shareSuccess="handleShareSuccess" />
  </div>
</template>

<script setup>
/* eslint-disable */
import { computed, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { toastService } from '@/utils/toastService'
import AnnApi from '@/config/annActApi'
import LoginDialog from './components/LoginDialog/index.vue'
import Milestone from './components/Milestone/index.vue'
import Contribute from './components/Contribute/index.vue'
import Hero from './components/Hero/index.vue'
import AnnLang from './components/AnnLang/index.vue'
import PowerContributedDialog from './components/PowerContributedDialog/index.vue'
import HowToPlayDialog from './components/HowToPlayDialog/index.vue'
import CongratulationDialog from './components/CongratulationDialog/index.vue'
import RewardHistoryDialog from './components/RewardHistoryDialog/index.vue'
import FlameSupplyDialog from './components/FlameSupplyDialog/index.vue'
import EliteRallyDialog from './components/EliteRallyDialog/index.vue'
import ShareDialog from './components/ShareDialog/index.vue'
import AnnFooter from './components/AnnFooter/index.vue'
import ta from 'thinkingdata-browser'
import { useLog } from '@/composables/useActLog'
import { GLOBAL_STAGE_REWARDS, RECALL_CARDS } from './const'
import { DESIGN_ROWS } from './components/Milestone/const'
import { pickRewardList, toTrackRewards } from './utils/reward'
import './style/font.css'

const store = useStore()
const route = useRoute()
const router = useRouter()
const { t, locale } = useI18n()
const log = useLog()
const ANN_BODY_CLASS = 'ann2026'
const ANN_FONT_CLASS_MAP = {
  vi: 'ann-font-dino',
  en: 'ann-font-dino',
  id: 'ann-font-dino',
  sg: 'ann-font-dino',
  my: 'ann-font-dino',
  ph: 'ann-font-dino',
  th: 'ann-font-sriracha',
  zh: 'ann-font-cn',
  cn: 'ann-font-cn',
  hk: 'ann-font-cn',
  tw: 'ann-font-cn',
}
const ANN_FONT_CLASSES = [...new Set(Object.values(ANN_FONT_CLASS_MAP))]
const getAnnFontClass = (lang) => ANN_FONT_CLASS_MAP[lang] || 'ann-font-dino'

document.body.classList.add(ANN_BODY_CLASS)

log.track('act_anniversary_page', {
  v1: store.state.actUserInfo?.uid || '',
})

const showLogin = ref(false)
const showPowerContributed = ref(false)
const powerContributedCount = ref(0)
const showHowToPlay = ref(false)
const showCongratulation = ref(false)
const congratulationRewards = ref([])
const congratulationTitleText = ref('')
const showRewardHistory = ref(false)
const showFlameSupply = ref(false)
const showEliteRally = ref(false)
const showShare = ref(false)
const guestGlobalStages = ref([])
const taskList = ref([])
const milestoneList = ref([])
const recallInfo = ref(null)
const rewardRecords = ref([])

const actUserInfo = computed(() => store.state.actUserInfo || {})
const isLoggedIn = computed(() => !!actUserInfo.value?.token)
const activityInfo = computed(() => store.state.annAct?.activityInfo)
const commonConfig = computed(() => store.state.annAct?.commonConfig)
const annFontClass = computed(() => getAnnFontClass(locale.value))
const globalStages = computed(() => {
  if (isLoggedIn.value && activityInfo.value?.globalStages?.length) {
    return activityInfo.value.globalStages
  }
  if (guestGlobalStages.value.length) return guestGlobalStages.value
  return commonConfig.value?.globalStages || []
})

const displayMilestones = computed(() => {
  const commonMilestones = commonConfig.value?.milestoneList || []
  if (!isLoggedIn.value || !milestoneList.value.length) return commonMilestones

  const commonByCode = new Map(
    commonMilestones.map((item) => [item.rewardCode, item]),
  )
  return milestoneList.value.map((item, index) => {
    const commonItem = commonByCode.get(item.rewardCode) || commonMilestones[index] || {}
    return {
      ...commonItem,
      ...item,
      rewardItems: item.rewardItems?.length
        ? item.rewardItems
        : commonItem.rewardItems || [],
    }
  })
})

// 未登录时用 commonConfig.taskFireCountMap 展示任务配置（进度为 0）
const displayTasks = computed(() => {
  if (isLoggedIn.value && taskList.value.length) return taskList.value
  const map = commonConfig.value?.taskFireCountMap || {}
  return Object.keys(map).map((taskType) => ({
    taskType,
    progress: 0,
    target: 1,
    fireCount: map[taskType],
    status: 0,
    isDaily: String(taskType).includes('daily'),
  }))
})

// 有已完成但未领取的任务（status=1）时展示火力补给角标
const hasClaimableTask = computed(() =>
  displayTasks.value.some((task) => Number(task.status) === 1),
)

const statusText = (status) => {
  if (status === 2) return t('ann2026.received')
  if (status === 1) return t('ann2026.receive')
  return t('ann2026.notReady')
}

const isShareTask = (taskType) => String(taskType || '') === 'ann_weekly_share'

const globalStageBtnText = (stage) => {
  if (!stage?.unlocked) return t('ann2026.locked')
  if (stage.received) return t('ann2026.received')
  return t('ann2026.receive')
}

const initLogin = async () => {
  const tokenHash = route.query?.tokenHash
  const uid = route.query?.uid
  if (tokenHash && uid) {
    router.replace({
      query: {
        ...route.query,
        uid: undefined,
        tokenHash: undefined,
      },
    })
    await AnnApi.gameTokenLogin({
      uid,
      tokenHash,
    })
  }
}

const loadCommon = async () => {
  try {
    await store.dispatch('annAct/fetchCommonConfig')
  } catch (e) {
    console.error(e)
  }
}

const loadGlobalProgress = async () => {
  try {
    const res = await AnnApi.getGlobalProgress({})
    guestGlobalStages.value = res?.globalStages || []
  } catch (e) {
    console.error(e)
  }
}

const loadActivity = async () => {
  if (!isLoggedIn.value) return
  try {
    await store.dispatch('annAct/fetchActivityInfo')
  } catch (e) {
    console.error(e)
  }
}

const loadTasks = async () => {
  if (!requireLogin()) return
  try {
    const res = await AnnApi.getTaskList({})
    taskList.value = res?.taskList || []
  } catch (e) {
    console.error(e)
  }
}

const loadMilestones = async () => {
  if (!requireLogin()) return
  try {
    const res = await AnnApi.getMilestoneList({})
    milestoneList.value = res?.milestoneList || []
  } catch (e) {
    console.error(e)
  }
}

const loadRecall = async () => {
  if (!requireLogin()) return
  try {
    recallInfo.value = await AnnApi.getRecallInfo({})
  } catch (e) {
    console.error(e)
  }
}

const loadRecords = async () => {
  if (!requireLogin()) return
  try {
    const res = await AnnApi.getRewardRecord({})
    rewardRecords.value = res?.recordList || []
  } catch (e) {
    console.error(e)
  }
}

const initGuestData = async () => {
  await Promise.all([loadCommon(), loadGlobalProgress()])
}

const initData = async () => {
  await loadActivity()
  await Promise.all([loadTasks(), loadMilestones(), loadRecall()])
}

const FIRST_LOGIN_STORAGE_KEY = 'ann2026_first_login_received'
const FIRST_LOGIN_SOURCE = 'first_login'
/** 本页会话内已处理完的账号（含已领/确认无需再领），防多入口重复触发 */
const firstLoginHandledAccounts = new Set()
/** 本页会话内正在领取中的账号 */
const firstLoginInFlightAccounts = new Set()

const getActAccountId = () =>
  String(
    actUserInfo.value?.cpUserId ||
      actUserInfo.value?.uid ||
      actUserInfo.value?.uin ||
      actUserInfo.value?.accountId ||
      '',
  )

const readFirstLoginCache = () => {
  try {
    const raw = localStorage.getItem(FIRST_LOGIN_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : {}
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

const hasFirstLoginCached = (accountId) =>
  !!(accountId && readFirstLoginCache()[accountId])

const markFirstLoginCached = (accountId) => {
  if (!accountId) return
  const cache = readFirstLoginCache()
  cache[accountId] = 1
  localStorage.setItem(FIRST_LOGIN_STORAGE_KEY, JSON.stringify(cache))
  firstLoginHandledAccounts.add(accountId)
}

const findFirstLoginRecord = (recordList = []) =>
  recordList.find((item) => item?.rewardSource === FIRST_LOGIN_SOURCE)

/**
 * 自动领见面礼（多入口共用，按账号防重）：
 * 1) localStorage 已领过 → 跳过
 * 2) 领奖记录有 first_login → 写缓存并跳过
 * 3) 否则调领取接口并弹窗
 */
const tryAutoClaimFirstLogin = async () => {
  if (!isLoggedIn.value) return
  const accountId = getActAccountId()
  if (!accountId) return
  if (
    hasFirstLoginCached(accountId) ||
    firstLoginHandledAccounts.has(accountId) ||
    firstLoginInFlightAccounts.has(accountId)
  ) {
    return
  }

  firstLoginInFlightAccounts.add(accountId)
  try {
    // 活动信息若已标明领过，直接缓存，少打一次记录接口
    if (activityInfo.value?.firstLoginRewardReceived) {
      markFirstLoginCached(accountId)
      return
    }

    const recordRes = await AnnApi.getRewardRecord({})
    const existed = findFirstLoginRecord(recordRes?.recordList || [])
    if (existed) {
      markFirstLoginCached(accountId)
      return
    }

    const claimRes = await AnnApi.receiveFirstLoginReward({})
    markFirstLoginCached(accountId)
    log.track('act_anni_welcome_reward', { v1: toTrackRewards(claimRes) })
    showReceivedRewards(claimRes)
    await loadActivity()
  } catch (e) {
    // 已领取：写入缓存，避免下次重复查
    if (Number(e?.code) === 2801) {
      markFirstLoginCached(accountId)
    }
    console.error(e)
  } finally {
    firstLoginInFlightAccounts.delete(accountId)
  }
}

const onLoginSuccess = () => {
  // 登录态变更由下方 watch 统一 initData + 领见面礼，避免与弹窗回调重复触发
  showLogin.value = false
}

const onLogout = () => {
  store.commit('SET_ACT_USER_INFO', {})
  store.commit('annAct/SET_ACTIVITY_INFO', null)
  taskList.value = []
  milestoneList.value = []
  recallInfo.value = null
  rewardRecords.value = []
  showLogin.value = false
  loadGlobalProgress()
}

const requireLogin = () => {
  if (!isLoggedIn.value) {
    showLogin.value = true
    return false
  }
  return true
}

// TODO: FLAME SUPPLY / ELITE RALLY / REWARD HISTORY 三个弹窗待开发
const openSupplyDialog = async () => {
  if (!requireLogin()) return
  log.track('enter_anni_fireapply', {})
  await loadTasks()
  showFlameSupply.value = true
}
const openRallyDialog = async () => {
  if (!requireLogin()) return
  log.track('enter_anni_teamup', {})
  await loadRecall()
  showEliteRally.value = true
}
const openHistoryDialog = async () => {
  if (!requireLogin()) return
  log.track('enter_anni_rewardslist', {})
  await loadRecords()
  showRewardHistory.value = true
}
// TODO: HOW TO PLAY 弹窗、充值入口、客服入口待开发
const openHowToPlayDialog = () => {
  showHowToPlay.value = true
}
const openRecharge = () => {}
const openService = () => {}

const showReceivedRewards = (response, fallbackRewardCode = '') => {
  let rewards = Array.isArray(response)
    ? response
    : response?.rewardItems ||
      response?.rewardList ||
      response?.rewards ||
      response?.items ||
      null

  // 兼容仅返回 propSnapshot JSON 的情况
  if ((!rewards || !rewards.length) && response?.propSnapshot) {
    rewards = [{ propSnapshot: response.propSnapshot }]
  }
  if (!rewards) rewards = []

  congratulationRewards.value = Array.isArray(rewards) ? rewards : [rewards]
  if (!congratulationRewards.value.length && fallbackRewardCode) {
    congratulationRewards.value = [{ rewardCode: fallbackRewardCode }]
  }
  congratulationTitleText.value = ''
  showFlameSupply.value = false
  showEliteRally.value = false
  showCongratulation.value = true
}

const handleIgnite = async () => {
  if (!requireLogin()) return
  try {
    const res = await AnnApi.ignite({})
    // 点火一次投入当前全部机会，v1 上报本次投入数
    log.track('anni_fireup', { v1: Number(res?.consumeCount ?? 0) })
    powerContributedCount.value = Number(res?.consumeCount ?? 0)
    showCongratulation.value = false
    showPowerContributed.value = true
    await initData()
  } catch (e) {
    console.error(e)
  }
}

const openShareDialog = () => {
  showShare.value = true
}

const handleShareSuccess = async () => {
  toastService.show(t('ann2026.shareOk'))

  // 仅登录且分享任务未完成时上报，用于完成分享任务
  if (!isLoggedIn.value) return
  const shareTask = displayTasks.value.find((task) =>
    isShareTask(task.taskType),
  )
  if (!shareTask || Number(shareTask.status) !== 0) return

  try {
    await AnnApi.share({})
    // 仅分享任务由前端感知完成时机，其余任务由服务端判定
    log.track('anni_task_complete', { v1: shareTask.taskType })
    await loadTasks()
  } catch (e) {
    console.error(e)
  }
}

const handleFirstLoginReward = async () => {
  await tryAutoClaimFirstLogin()
}

const handleReceiveTask = async (taskType) => {
  if (!requireLogin()) return
  const fireCount = displayTasks.value.find(
    (task) => task.taskType === taskType,
  )?.fireCount
  try {
    await AnnApi.receiveTaskReward({ taskType })
    log.track('anni_get_fire', { v1: taskType, v2: Number(fireCount ?? 0) })
    toastService.show(t('ann2026.receiveOk'))
    await initData()
  } catch (e) {
    console.error(e)
  }
}

const handleReceiveGlobal = async (stage) => {
  if (!requireLogin()) return
  const percent = globalStages.value.find(
    (item) => Number(item.stage) === Number(stage),
  )?.percent
  try {
    await AnnApi.receiveGlobalReward({ stage })
    log.track('anni_progress_rewards', {
      v1: toTrackRewards(GLOBAL_STAGE_REWARDS[stage] || []),
      v2: Number(percent ?? 0),
    })
    toastService.show(t('ann2026.receiveOk'))
    await loadActivity()
  } catch (e) {
    console.error(e)
  }
}

const handleReceiveMilestone = async (rewardCode) => {
  if (!requireLogin()) return
  const milestone = displayMilestones.value.find(
    (item) => item.rewardCode === rewardCode,
  )
  const targetCount = Number(milestone?.targetCount ?? 0)
  // 接口未回奖励明细时落到设计稿配置（与 Milestone 组件同一套兜底）
  const rewards = pickRewardList(milestone)
  const fallback = DESIGN_ROWS.find((row) => row.target === targetCount)?.reward
  try {
    await AnnApi.receiveMilestone({ rewardCode })
    log.track('anni_cnt_rewards', {
      v1: toTrackRewards(
        rewards.length ? rewards : fallback ? [fallback] : [],
      ),
      v2: targetCount,
    })
    toastService.show(t('ann2026.receiveOk'))
    await loadMilestones()
  } catch (e) {
    console.error(e)
  }
}

const handleReceiveRecall = async (rewardCode) => {
  if (!requireLogin()) return
  const apiCard = (recallInfo.value?.rewardList || []).find(
    (item) => item.rewardCode === rewardCode,
  )
  const fallbackCard = RECALL_CARDS.find(
    (item) => item.rewardCode === rewardCode,
  )
  const rewards = pickRewardList(apiCard)
  try {
    await AnnApi.receiveRecallReward({ rewardCode })
    log.track('anni_friends_rewards', {
      v1: toTrackRewards(
        rewards.length ? rewards : fallbackCard?.rewards || [],
      ),
      v2: Number(apiCard?.needCount ?? fallbackCard?.needCount ?? 0),
    })
    toastService.show(t('ann2026.receiveOk'))
    await loadRecall()
  } catch (e) {
    console.error(e)
  }
}

initLogin()
initGuestData()

// 统一入口：已有登录态进页 / 弹窗登录 / tokenHash 换票后刷新，都走这里领见面礼
watch(
  () => [isLoggedIn.value, getActAccountId()],
  async ([loggedIn, accountId]) => {
    if (!loggedIn || !accountId) return
    // 绑定 TA 账号，使后续事件带上 #account_id
    if (actUserInfo.value?.uid) ta.login(actUserInfo.value.uid)
    await initData()
    await tryAutoClaimFirstLogin()
  },
  { immediate: true },
)

watch(
  annFontClass,
  (fontClass) => {
    document.body.classList.remove(...ANN_FONT_CLASSES)
    document.body.classList.add(fontClass)
  },
  { immediate: true },
)

onUnmounted(() => {
  document.body.classList.remove(ANN_BODY_CLASS, ...ANN_FONT_CLASSES)
})
</script>

<style lang="less" scoped>
:global(body.ann2026.ann-font-dino) {
  font-family: 'ann2026-dino', Arial, sans-serif;
}

:global(body.ann2026.ann-font-sriracha) {
  font-family: 'ann2026-sriracha', Arial, sans-serif;
}

:global(body.ann2026.ann-font-cn) {
  font-family: 'ann2026-cn-heavy', 'Microsoft YaHei', sans-serif;
}

:global(body.ann2026 .pc.global-toast) {
  font-size: 24px !important;
  padding: 10px 20px !important;
  border-radius: 10px !important;
}

.ann-page {
  position: relative;
  min-height: 100vh;
  background: #5041ac;
  color: #f2f4f8;
  font-size: 14px;
  display: flex;
  justify-content: center;
  overflow-x: hidden;
  isolation: isolate;

  &.ann-font-dino {
    font-family: 'ann2026-dino', Arial, sans-serif;
  }

  &.ann-font-sriracha {
    font-family: 'ann2026-sriracha', Arial, sans-serif;
  }

  &.ann-font-cn {
    font-family: 'ann2026-cn-heavy', 'Microsoft YaHei', sans-serif;
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    width: 752px;
    height: 3270px;
    transform: translateX(-50%);
    pointer-events: none;
  }

  // Layer 665 在画板内从 y=365 开始，保持整页坐标，不再按区块重复铺设
  &::before {
    top: 365px;
    height: 2905px;
    z-index: 0;
    background-image: url('~@/assets/imgs/act/ann2026/common/page-texture.png');
    background-position: center top;
    background-size: 752px 2905px;
    background-repeat: no-repeat;
    opacity: 0.1;
  }

  &::after {
    z-index: 1;
    background: url('~@/assets/imgs/act/ann2026/common/page-gradient.png') center top / 752px 3270px no-repeat;
  }
}

.ann-inner {
  width: 752px;
  max-width: 100%;
  position: relative;
  z-index: 2;
  overflow-x: hidden;
}

</style>
